import React, { useState, useEffect, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Clock,
  Sparkles,
  Users,
  MoreVertical,
  Circle,
  MessageSquare,
  CheckCircle,
  FileText,
  X,
  Check,
  User,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../../theme/useTheme";

import { Card } from "../../components/Card";
import { Badge } from "../../components/Badge";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import SuccessPopup from "../../components/SuccessPopup";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { DataTable } from "../../components/DataTable";
import { UnifiedModal } from "../../components/UnifiedModal";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Button } from "../../components/Button";
import { AgencyIcon } from "../../components/icons/AgencyIcon";
import { settingsPath } from "../Settings/settingsPaths";

const useMarketingPlans = (
  interceptorReady,
  shouldFetch,
  filters = {},
  refreshDeps = [filtersRefreshKey]
) => {
  const queryParams = new URLSearchParams();

  if (filters.search) {
    queryParams.append("search", filters.search);
  }

  if (filters.status && filters.status !== "all") {
    queryParams.append("status", filters.status);
  }

  if (filters.page) {
    queryParams.append("page", filters.page);
  }

  if (filters.page_size) {
    queryParams.append("page_size", filters.page_size);
  }

  if (filters.agency) {
    queryParams.append("agency", filters.agency);
  }

  const endpoint = `/leadmagnet/strategies/${queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

  return getData(endpoint, interceptorReady && shouldFetch, refreshDeps);
};

const statusConfig = {
  new: {
    label: "Nuevo",
    color: "red",
  },
  contacted: {
    label: "Contactado",
    color: "yellow",
  },
  converted: {
    label: "Convertido",
    color: "green",
  },
};

export function PlanMarketing() {
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    filterRefresh,
    marketingPlanRefresh,
    changeSelectedFilter,
  } = useOutletContext();
  const navigate = useNavigate();
  const { user: currentUser, interceptorReady } = useAuth();
  const theme = useTheme();
  const { neutrals } = useCustomTheme();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [refresh, setRefresh] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [allPlans, setAllPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusStats, setStatusStats] = useState(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedPlanForMenu, setSelectedPlanForMenu] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);

  const [isNewPlanModalOpen, setIsNewPlanModalOpen] = useState(false);
  const [newPlanInfo, setNewPlanInfo] = useState("");
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [createdStrategyId, setCreatedStrategyId] = useState(null);
  const [createdStatus, setCreatedStatus] = useState(null);
  const [createdError, setCreatedError] = useState("");

  const [popupState, setPopupState] = useState({
    isOpen: false,
    message: "",
    error: false,
  });
  const isSuperadmin = currentUser?.role === "superadmin";
  const showCreatorColumn =
    currentUser?.role === "agency_admin" || currentUser?.role === "consultant";

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed === "" && lastSearchRef.current !== "") {
        lastSearchRef.current = "";
        setSearchTerm("");
        setSearchRefreshKey((prev) => prev + 1);
        return;
      }

      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const filters = React.useMemo(() => {
    return {
      search: searchTerm,
      status: selectedStatus,
      page: currentPage + 1,
      page_size: pageSize,
      agency: currentUser?.role === "superadmin" ? selectedFilter : null,
    };
  }, [searchTerm, selectedStatus, currentPage, pageSize, selectedFilter, currentUser]);

  const { data: backendPlans, isLoading: isLoadingPlans } = useMarketingPlans(
    interceptorReady,
    !!currentUser,
    filters,
    [
      refresh,
      searchTerm,
      selectedStatus,
      currentPage,
      pageSize,
      searchRefreshKey,
      filterRefresh,
      marketingPlanRefresh,
    ]
  );

  useEffect(() => {
    if (backendPlans) {
      if (backendPlans.results) {
        setAllPlans(backendPlans.results);
        setTotalCount(backendPlans.count || 0);
        setStatusStats(backendPlans.status_stats || null);
      } else if (Array.isArray(backendPlans)) {
        setAllPlans(backendPlans);
        setTotalCount(backendPlans.length);
        setStatusStats(null);
      } else {
        setAllPlans([]);
        setTotalCount(0);
        setStatusStats(null);
      }
    }
  }, [backendPlans]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedStatus, selectedFilter]);

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
  };

  const handleStatusChange = (status) => {
    if (status === selectedStatus) return;
    setSelectedStatus(status);
    setRefresh((prev) => prev + 1);
  };

  const handleOpenPlan = (plan) => {
    if (!plan?.uuid) return;
    navigate(
      `/HerramientasIA/planes/${plan.uuid}?hideForm=true&returnTo=plan-marketing`
    );
  };

  const handleMenuOpen = (event, plan) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedPlanForMenu(plan);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedPlanForMenu(null);
  };

  const menuOpen = Boolean(menuAnchorEl);

  const updatePlanStatus = async (planId, newStatus) => {
    try {
      setIsUpdatingStatus(planId);
      await apiClient.patch(`/leadmagnet/strategies/${planId}/`, {
        lead_status: newStatus,
      });
      setAllPlans((prevPlans) =>
        prevPlans.map((plan) =>
          plan.uuid === planId ? { ...plan, lead_status: newStatus } : plan
        )
      );
      setStatusStats((prevStats) => {
        if (!prevStats) return prevStats;
        const previousStatus = selectedPlanForMenu?.lead_status;
        if (!previousStatus || previousStatus === newStatus) {
          return prevStats;
        }
        const nextStats = { ...prevStats };
        if (typeof nextStats[previousStatus] === "number") {
          nextStats[previousStatus] = Math.max(0, nextStats[previousStatus] - 1);
        }
        if (typeof nextStats[newStatus] === "number") {
          nextStats[newStatus] = nextStats[newStatus] + 1;
        }
        return nextStats;
      });
      const statusLabels = {
        new: "Nuevo",
        contacted: "Contactado",
        converted: "Convertido",
      };
      setPopupState({
        isOpen: true,
        message: `Lead marcado como ${statusLabels[newStatus]}`,
        error: false,
      });
    } catch (error) {
      console.error("Error actualizando estado del plan:", error);
      setPopupState({
        isOpen: true,
        message:
          error.response?.data?.message || "No se pudo actualizar el estado del lead",
        error: true,
      });
    } finally {
      setIsUpdatingStatus(null);
      handleMenuClose();
    }
  };

  const handleCreatePlan = async () => {
    const trimmedInfo = newPlanInfo.trim();
    if (!trimmedInfo) {
      setPopupState({
        isOpen: true,
        message: "Añade información del cliente antes de generar el plan",
        error: true,
      });
      return;
    }

    try {
      setIsCreatingPlan(true);
      const response = await apiClient.post("/leadmagnet/strategies/", {
        client_info: trimmedInfo,
      });
      const strategyId = response?.data?.uuid;
      if (strategyId) {
        setCreatedStrategyId(strategyId);
        setCreatedStatus("processing");
        setCreatedError("");
        setRefresh((prev) => prev + 1);
        return;
      }

      setPopupState({
        isOpen: true,
        message: "No se recibió el identificador del plan generado",
        error: true,
      });
    } catch (error) {
      console.error("Error creating marketing plan:", error);
      setPopupState({
        isOpen: true,
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "No se pudo generar el plan",
        error: true,
      });
    } finally {
      setIsCreatingPlan(false);
    }
  };

  const resetNewPlanModal = () => {
    setIsNewPlanModalOpen(false);
    setNewPlanInfo("");
    setCreatedStrategyId(null);
    setCreatedStatus(null);
    setCreatedError("");
  };

  const resetCreationState = () => {
    setCreatedStrategyId(null);
    setCreatedStatus(null);
    setCreatedError("");
  };

  useEffect(() => {
    if (!isNewPlanModalOpen || !createdStrategyId || createdStatus !== "processing") {
      return undefined;
    }

    const intervalId = setInterval(async () => {
      try {
        const response = await apiClient.get(
          `/leadmagnet/strategy/${createdStrategyId}`
        );
        const data = response.data;
        if (data?.status === "completed") {
          setCreatedStatus("completed");
          clearInterval(intervalId);
        } else if (data?.status === "failed") {
          setCreatedStatus("failed");
          setCreatedError(data?.error_message || "Error al generar la estrategia");
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error polling strategy:", error);
      }
    }, 1200);

    return () => clearInterval(intervalId);
  }, [isNewPlanModalOpen, createdStrategyId, createdStatus]);

  useEffect(() => {
    if (!currentUser) return;

    const canCreatePlan =
      currentUser?.role === "superadmin" ||
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant";

    const rightContent = (
      <Stack direction="row" spacing={1.5} alignItems="center">
        {isSuperadmin && (
          <Button
            variant="white"
            onClick={() => navigate(settingsPath("prompts"))}
          >
            <Sparkles size={16} />
            Configurar prompts
          </Button>
        )}
        {canCreatePlan && (
          <Button onClick={() => setIsNewPlanModalOpen(true)}>
            <Sparkles size={16} />
            Nuevo plan de Marketing
          </Button>
        )}
      </Stack>
    );

    setPageContext({
      title: "Planes de Marketing",
      subtitle: "Planes generados desde la plataforma",
      showTitle: true,
      leftContent: null,
      rightContent,
      breadcrumbItems:
        isSuperadmin
          ? selectedFilter && selectedFilter !== "global"
            ? [
              {
                label: "Vista global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Agencia: ${selectedFilterData?.name || "Agencia"}` },
            ]
            : [{ label: "Vista global" }]
          : [],
    });
  }, [
    currentUser,
    setPageContext,
    selectedFilter,
    selectedFilterData,
    navigate,
    theme,
    isSuperadmin,
    changeSelectedFilter,
  ]);

  const plans = React.useMemo(() => {
    if (!allPlans) return [];

    return allPlans.map((plan) => ({
      uuid: plan.uuid,
      client_information: plan.client_information || "",
      lead_status: plan.lead_status || "new",
      agency_id: plan.agency_id || null,
      agency_name: plan.agency_name || null,
      created_by_name: plan.created_by_name || null,
      created_at: plan.created_at,
    }));
  }, [allPlans]);

  const planColumns = [
    {
      key: "client_information",
      header: "Informacion del cliente",
      width: "100%",
      minWidth: { xs: "220px", md: "360px" },
      cellSx: { maxWidth: 0 },
      render: (plan) => {
        const displayInfo = plan.client_information || "Sin informacion";
        return (
          <Tooltip title={displayInfo} arrow>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {displayInfo}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      key: "agency_creator",
      header: "Agencia/Creador",
      width: 220,
      minWidth: { xs: "160px", md: "220px" },
      hidden: !isSuperadmin,
      render: (plan) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {plan.agency_name ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 24,
                  width: 24,
                  flexShrink: 0,
                }}
              >
                <AgencyIcon size={19} color={theme.palette.text.secondary} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                }}
              >
                {plan.agency_name}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24,
                width: 24,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                -
              </Typography>
            </Box>
          )}
          <Tooltip title={plan.created_by_name || "-"} arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 24,
                  width: 24,
                  flexShrink: 0,
                }}
              >
                <User size={12} color={theme.palette.text.secondary} />
              </Box>
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                }}
              >
                {plan.created_by_name || "-"}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: "creator",
      header: "Creador",
      width: 180,
      minWidth: { xs: "140px", md: "180px" },
      hidden: isSuperadmin || !showCreatorColumn,
      render: (plan) => (
        <Tooltip title={plan.created_by_name || "-"} arrow>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24,
                width: 24,
                flexShrink: 0,
              }}
            >
              <User size={12} color={theme.palette.text.secondary} />
            </Box>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {plan.created_by_name || "-"}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "status",
      header: "Estado",
      width: 140,
      minWidth: { xs: "120px", md: "150px" },
      render: (plan) => {
        const statusInfo = statusConfig[plan.lead_status] || statusConfig.new;
        return (
          <Badge variant="outline" color={statusInfo.color}>
            {statusInfo.label}
          </Badge>
        );
      },
    },
    {
      key: "created_at",
      header: "Fecha",
      width: 150,
      minWidth: { xs: "120px", md: "150px" },
      render: (plan) => (
        <Tooltip
          title={new Date(plan.created_at).toLocaleDateString(
            "es-ES",
            {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }
          )}
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Clock size={12} color={theme.palette.text.secondary} />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {new Date(plan.created_at).toLocaleDateString(
                "es-ES",
                {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "center",
      width: "50px",
      minWidth: { xs: "40px", md: "50px" },
      render: (plan) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, plan)}
            sx={{
              width: "32px",
              height: "32px",
              padding: 0,
              flexShrink: 0,
            }}
          >
            {isUpdatingStatus === plan.uuid ? (
              <CircularProgress size={16} />
            ) : (
              <MoreVertical size={16} />
            )}
          </IconButton>
          {menuOpen && selectedPlanForMenu?.uuid === plan.uuid && (
            <DropdownMenuContent
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              {plan.lead_status !== "contacted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePlanStatus(plan.uuid, "contacted");
                  }}
                  disabled={isUpdatingStatus === plan.uuid}
                >
                  <MessageSquare size={16} />
                  Lead Contactado
                </DropdownMenuItem>
              )}
              {plan.lead_status !== "converted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePlanStatus(plan.uuid, "converted");
                  }}
                  disabled={isUpdatingStatus === plan.uuid}
                >
                  <CheckCircle size={16} />
                  Lead Convertido
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPlan(plan);
                  handleMenuClose();
                }}
              >
                <FileText size={16} />
                Ver Estrategia
              </DropdownMenuItem>
            </DropdownMenuContent>
          )}
        </Box>
      ),
    },
  ];

  const getStatusCount = (key) => (statusStats ? statusStats[key] || 0 : undefined);

  return (
    <>
      <DataTable
        columns={planColumns}
        rows={plans}
        getRowId={(plan) => plan.uuid}
        loading={isLoadingPlans}
        emptyIcon={Users}
        emptyMessage={
          searchTerm
            ? "No se encontraron planes con los filtros"
            : "No hay planes disponibles"
        }
        onRowClick={(plan) => handleOpenPlan(plan)}
        tableMinWidth={350}
        tableSx={{ tableLayout: "fixed" }}
        maxHeight="calc(100vh - 285px)"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar planes..."
        tabs={[
          { value: "all", label: "Todos", count: getStatusCount("all") },
          { value: "new", label: "Nuevos", count: getStatusCount("new") },
          { value: "contacted", label: "Contactados", count: getStatusCount("contacted") },
          { value: "converted", label: "Convertidos", count: getStatusCount("converted") },
        ]}
        activeTab={selectedStatus}
        onTabChange={handleStatusChange}
        page={currentPage}
        rowsPerPage={pageSize}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <UnifiedModal
        open={isNewPlanModalOpen}
        onClose={resetNewPlanModal}
        title="Nuevo plan de Marketing"
        maxWidth="md"
      >
        {createdStatus === "processing" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              minHeight: 280,
              textAlign: "center",
            }}
          >
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Generando plan de marketing
            </Typography>
            <Typography variant="body2" sx={{ color: neutrals.gray[500], maxWidth: 420 }}>
              La estrategia se está generando. Mantén esta ventana abierta mientras finaliza.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
              <Button variant="outline" onClick={resetNewPlanModal}>
                Cerrar
              </Button>
            </Box>
          </Box>
        ) : createdStatus === "completed" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              minHeight: 280,
              textAlign: "center",
            }}
          >
            <Check size={48} color={theme.palette.success.main} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Plan generado correctamente
            </Typography>
            <Typography variant="body2" sx={{ color: neutrals.gray[500], maxWidth: 420 }}>
              Ya puedes revisar la estrategia completa.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
              <Button variant="outline" onClick={resetNewPlanModal}>
                Cerrar
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    `/HerramientasIA/planes/${createdStrategyId}?hideForm=true&returnTo=plan-marketing`
                  )
                }
              >
                Ver estrategia
              </Button>
            </Box>
          </Box>
        ) : createdStatus === "failed" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              minHeight: 280,
              textAlign: "center",
            }}
          >
            <X size={48} color={theme.palette.error.main} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Error en la generación
            </Typography>
            <Typography variant="body2" sx={{ color: neutrals.gray[500], maxWidth: 420 }}>
              {createdError || "No se pudo generar la estrategia. Inténtalo de nuevo."}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
              <Button variant="outline" onClick={resetNewPlanModal}>
                Cerrar
              </Button>
              <Button onClick={resetCreationState}>
                Reintentar
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 0.5,
              }}
            >
              <Sparkles size={36} color={theme.palette.primary.main} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Crear nuevo plan
              </Typography>
              <Typography variant="body2" sx={{ color: neutrals.gray[500] }}>
                Se recomienda incluir la mayor cantidad de detalles posible sobre el negocio, como el sector, el público objetivo, los objetivos comerciales, el contexto del mercado y cualquier otra información relevante. Cuanto más completa sea la información, más precisa será la estrategia.
              </Typography>
            </Box>
            <CustomTextArea
              placeholder="Describe el negocio, objetivos y cualquier informacion clave..."
              value={newPlanInfo}
              onChange={(e) => setNewPlanInfo(e.target.value)}
              rows={10}
              sx={{ minHeight: "240px", resize: "none" }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 1 }}>
              <Button variant="outline" onClick={resetNewPlanModal}>
                Cancelar
              </Button>
              <Button onClick={handleCreatePlan} disabled={isCreatingPlan}>
                <Sparkles size={16} />
                Generar plan
              </Button>
            </Box>
          </Box>
        )}
      </UnifiedModal>

      <SuccessPopup
        isOpen={popupState.isOpen}
        message={popupState.message}
        error={popupState.error}
        onClose={() => setPopupState({ ...popupState, isOpen: false })}
      />
    </>
  );
}
